import React from "react";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";

import { ReactComponent as ChevronIcon } from "Assets/icon/icon-chevron.svg";

import { useTranslation } from "lib/i18n/use-translation";
import styles from "apps/user/learner/styles/common.module.scss";

export const PageLoading = () => {
  return (
    <div className={styles["common-loading"]}>
      <div className={clsx("spinner-border", styles["spinner"])} />
    </div>
  );
};

interface PageErrorProps {
  children?: React.ReactNode;
  className?: string;
}

export const PageError = ({ children, className }: PageErrorProps) => {
  const { t } = useTranslation(["common"]);

  return (
    <div className={clsx(styles["common-error"], className && className)}>
      {children ? children : t("common:error.message")}
    </div>
  );
};

interface PageContentProps {
  maxWidth?: "sm" | "lg" | "100%";
  children: React.ReactNode;
  className?: string;
}

export const PageContent = ({
  maxWidth,
  children,
  className,
}: PageContentProps) => {
  let innerClassName = styles["common-content"];
  if (maxWidth === "sm") {
    innerClassName = clsx(innerClassName, styles["common-content-sm"]);
  } else if (maxWidth === "lg") {
    innerClassName = clsx(innerClassName, styles["common-content-lg"]);
  } else if (maxWidth === "100%") {
    innerClassName = clsx(innerClassName, styles["common-content-full"]);
  }

  return <div className={clsx(innerClassName, className)}>{children}</div>;
};

interface PageBaseHeaderProps {
  transparent?: boolean;
  children?: React.ReactNode;
  className?: string;
}

export const PageBaseHeader = ({
  transparent,
  children,
  className,
}: PageBaseHeaderProps) => {
  return (
    <div
      className={clsx(
        styles["common-header"],
        !transparent && styles["common-header-filled"],
        className
      )}
    >
      <div className={styles["common-title-div"]}>{children}</div>
    </div>
  );
};

interface PageHeaderProps extends PageBaseHeaderProps {
  title?: string;
  backTo?: string;
  showBackButton?: boolean;
  id?: string;
}

export const PageHeader = ({
  title,
  transparent,
  showBackButton,
  backTo,
  children,
  className,
}: PageHeaderProps) => {
  const navigate = useNavigate();

  const handleClickBack = () => {
    if (backTo) {
      navigate(backTo);
    } else {
      navigate(-1);
    }
  };

  return (
    <PageBaseHeader transparent={transparent} className={className}>
      {showBackButton && (
        <span
          onClick={handleClickBack}
          className={styles["back-icon-container"]}
        >
          <ChevronIcon
            alt="Arrow pointing left"
            color={transparent ? "white" : "black"}
          />
        </span>
      )}
      {title && <h1 className={styles["common-title"]}>{title}</h1>}
      {children && (
        <span className={styles["common-children"]}>{children}</span>
      )}
    </PageBaseHeader>
  );
};

interface PageContainerProps {
  children: React.ReactNode;
  background?: "white" | "transparent" | "off-white";
  className?: string;
  transparent?: boolean;
}

export const PageContainer = ({
  transparent,
  background = "transparent",
  children,
  className,
}: PageContainerProps) => {
  const getBackgroundConfig = (bg: string) => {
    const baseClassName = styles["common-page"];

    switch (bg) {
      case "off-white":
        return {
          innerClassName: clsx(baseClassName, styles["common-page-off-white"]),
          customStyle: {},
          outerCustomStyle: {},
        };

      case "transparent":
        return {
          innerClassName: baseClassName,
          customStyle: {},
          outerCustomStyle: {},
        };

      case "white":
      default:
        return {
          innerClassName: clsx(baseClassName, styles["common-page-white"]),
          customStyle: {},
          outerCustomStyle: {},
        };
    }
  };

  const { innerClassName, customStyle, outerCustomStyle } =
    getBackgroundConfig(background);

  return (
    <div
      id="page-container"
      className={clsx(styles["page-container"], className)}
      style={outerCustomStyle}
    >
      <div className={innerClassName} style={customStyle}>
        {children}
      </div>
    </div>
  );
};

export default PageContainer;
